import { Fragment, useState, useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { NMObjectHeaderReport } from "@neumetric/blueprint-lib/flows";
import { Col, NMPDFViewer } from "@neumetric/blueprint-lib/components";
import { getCurrentUser, isOrganizer, isGoverner } from "@neumetric/blueprint-lib/store";
import { DateTimeUtil } from "@neumetric/blueprint-lib/utility";
import { getCurrentRisk, riskActions, getRiskReport } from "../../store";

const MenuItems = [
  {
    icon: "SUBMIT",
    shouldCloseOnClick: true,
    id: "details",
    label: "View Risk Details",
    variant: "CONTRAST",
  },
];

export const RiskReport = ({ onShowDetails }) => {
  const dispatch = useDispatch();
  const _ref = useRef(null);
  const _initialized = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [zoom, setZoom] = useState(1.25);
  const user = useSelector(getCurrentUser);
  const riskReport = useSelector(getRiskReport);
  const currentRisk = useSelector(getCurrentRisk);
  const isOrganizerUser = isOrganizer(user);
  const isGovernerUser = isGoverner(user);
  const allowExport = isOrganizerUser || isGovernerUser;

  useEffect(() => {
    if (_initialized.current) return;
    let shouldGenerateReport = true;
    if (riskReport) {
      const reportGeneratedAt = new DateTimeUtil(riskReport.updated_at);
      shouldGenerateReport = !reportGeneratedAt.isAfter(currentRisk.updated_at);
    }
    _initialized.current = true;
    if (shouldGenerateReport) {
      generateReport();
    } else if (!currentRisk.report) {
      fetchReport();
    } else {
      handleComplete();
    }
  }, []);

  const handleComplete = () => {
    setShowReport(true);
  }
  const handleFailure = () => {
    setShowReport(false);
  }

  const fetchReport = useCallback(() => {
    if (!riskReport) return;
    const action = riskActions.fetchRiskReport({ url: riskReport.encrypt_evidence_url });
    action.onComplete = handleComplete;
    action.onFailure = handleFailure;
    dispatch(action);
  }, [riskReport]);

  const generateReport = () => {
    const timerId = setTimeout(() => {
      if (timerId) {
        clearTimeout(timerId);
      }
      const action = riskActions.createRiskReport();
      action.onComplete = () => {
        const timer = setTimeout(() => {
          if (timer) {
            clearTimeout(timer);
          }
          fetchReport();
        }, 500);
      };
      action.onFailure = handleFailure;
      dispatch(action);
    }, 1000);
  }

  const handleMenuClick = (item) => {
    if (item.id === "details") {
      onShowDetails();
    }
  };

  // const getPasswordPrefix = () => {
  //   const firstLetter = user.org.brandname.charAt(0).toUpperCase();
  //   const dateStr = new DateTimeUtil().toString("YYMM");
  //   return `${firstLetter}${dateStr}RPR`;
  // };

  const handleExport = (iPassword, iEmailAddress) => {
    let action = null;
    if (iEmailAddress) {
      action = riskActions.shareRiskReport({ emailAddress: iEmailAddress });
    } else {
      action = riskActions.exportRiskReport({});
    }
    if (action) {
      setIsSubmitting(true);
      action.onComplete = () => {
        if (_ref.current && _ref.current.resetMenu) {
          _ref.current.resetMenu();
        }
        setIsSubmitting(false);
      };
      action.onFailure = () => {
        setIsSubmitting(false);
      };
      dispatch(action);
    }
  };

  const handleZoomChange = (value) => {
    setZoom(value);
  }

  return (
    <Fragment>
      <NMObjectHeaderReport
        ref={_ref}
        object={currentRisk}
        classes={{
          root: "no-shadow mt-40",
          title: "text-heading",
        }}
        objectProps={{
          name: "Risk",
          idAttribute: "module_risk_id",
          titleAttribute: "title",
        }}
        formData={currentRisk}
        menu={{
          items: MenuItems,
          headerButton: { btnType: "CONTAINED", icon: "DOT_MENU" },
          onMenuClick: handleMenuClick,
        }}
        exports={
          allowExport && {
            showEncryption: false,
            filename: `Risk_Acceptance_Record_${currentRisk.module_risk_id}.pdf`,
            allowedDomains: user.org.email_domain.split(","),
            submitting: isSubmitting,
            onExport: handleExport,
          }
        }
        zoom={{
          options: [
            { label: "Fit Width", id: "page-width" },
            { label: "Fit Page", id: "page-fit" },
            { label: "100%", id: 1 },
            { label: "50%", id: 0.5 },
            { label: "125%", id: 1.25 },
            { label: "150%", id: 1.5 },
            { label: "200%", id: 2 },
          ],
          defaultValue: "page-width",
          onChange: handleZoomChange,
        }}
      />
      <Col className="mb-16 mt-1 w-100 flex-auto overflow-auto">
        <NMPDFViewer blob={showReport ? currentRisk.report : null} zoom={zoom} />
      </Col>
    </Fragment>
  );
};
